import { CommonModule } from '@angular/common';
import {
 Component, NgModule, Input, Output, EventEmitter, OnInit, OnDestroy, ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import {
 TemplateVariable, stripVariableSpans, escapeHtml, unescapeHtml
} from './template-variable.utils';
import { buildTemplateVariableSchema, preprocessHtmlForEditor } from './template-variable-node';

@Component({
    selector: 'teller-online-wysiwyg-editor',
    templateUrl: './wysiwyg-editor.component.html',
    styleUrls: ['./wysiwyg-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TellerOnlineWysiwygEditorComponent implements OnInit, OnDestroy {
    @Input() initialContent: string = '';

    @Input() placeholder: string = 'Enter content...';

    @Input() templateVariables: TemplateVariable[] = [];

    @Input() showHtmlToggle: boolean = false;

    @Input() plainText: boolean = false;

    @Input() allowAlignment: boolean = false;

    @Input() allowLists: boolean = true;

    /** Id of an external <label> for this editor (preferred over ariaLabel when available). */
    @Input() ariaLabelledby: string = '';

    /** Fallback accessible name when no visible <label> exists to reference. */
    @Input() ariaLabel: string = '';

    @Output() contentChange = new EventEmitter<string>();

    editor!: Editor;

    toolbar: Toolbar;

    content: string = '';

    rawContent: string = '';

    isHtmlMode: boolean = false;

    private hasTemplateVariables: boolean = false;

    ngOnInit(): void {
        // Plain-text mode (SMS) has no formatting toolbar and round-trips plain
        // text in/out of the editor. Without this the editor emits raw HTML into
        // the plain-text SMS body.
        if (!this.plainText) {
            this.toolbar = [
                ['bold', 'italic', 'underline'],
            ];
            if (this.allowLists) {
                this.toolbar.push(['bullet_list', 'ordered_list']);
            }
            if (this.allowAlignment) {
                this.toolbar.push(['align_left', 'align_center', 'align_right', 'align_justify']);
            }
            this.toolbar.push(['link']);
        }

        this.hasTemplateVariables = this.templateVariables.length > 0;
        this.rawContent = this.initialContent;

        // In plain text mode, convert the stored plain text to HTML for the editor.
        const htmlContent = this.plainText
            ? this.plainTextToHtml(this.initialContent)
            : this.initialContent;

        if (this.hasTemplateVariables) {
            const schema = buildTemplateVariableSchema(this.templateVariables);
            this.editor = new Editor({ schema });
            this.content = preprocessHtmlForEditor(htmlContent, this.templateVariables);
        } else {
            this.editor = new Editor();
            this.content = htmlContent;
        }
    }

    ngOnDestroy(): void {
        this.editor.destroy();
    }

    onContentChange(content: string): void {
        if (this.hasTemplateVariables) {
            this.rawContent = stripVariableSpans(content);
        } else {
            this.rawContent = content;
        }

        // Plain-text consumers (SMS) store plain text, not the editor's HTML.
        if (this.plainText) {
            this.rawContent = this.htmlToPlainText(this.rawContent);
        }

        this.contentChange.emit(this.rawContent);
    }

    onHtmlContentChange(rawContent: string): void {
        this.rawContent = rawContent;
        this.contentChange.emit(this.rawContent);
    }

    getContent(): string {
        return this.rawContent;
    }

    toggleHtmlMode(): void {
        if (this.isHtmlMode) {
            // HTML → WYSIWYG: pre-process raw content and load into editor
            if (this.hasTemplateVariables) {
                const preprocessed = preprocessHtmlForEditor(this.rawContent, this.templateVariables);
                this.content = preprocessed;
                this.editor.setContent(preprocessed);
            } else {
                this.content = this.rawContent;
                this.editor.setContent(this.rawContent);
            }
        } else if (this.hasTemplateVariables) {
            // WYSIWYG → HTML: post-process current content for display
            this.rawContent = stripVariableSpans(this.content);
        } else {
            this.rawContent = this.content;
        }
        this.isHtmlMode = !this.isHtmlMode;
    }

    insertVariable(variable: string): void {
        if (this.isHtmlMode) {
            this.rawContent += variable;
            this.contentChange.emit(this.rawContent);
            return;
        }

        const { state } = this.editor.view;
        const nodeType = state.schema.nodes.template_variable;
        if (!nodeType) {
            this.editor.commands.insertText(variable).exec();
            this.onContentChange(this.content);
            return;
        }

        const variableInfo = this.templateVariables.find(v => v.value === variable);
        const node = nodeType.create({
            value: variable,
            label: variableInfo?.label || null,
            description: variableInfo?.description || null,
        });
        const { from, to } = state.selection;
        // Cast to any to work around duplicate prosemirror-model type conflict
        // (prosemirror-transform bundles its own copy of prosemirror-model)
        const tr = state.tr.replaceWith(from, to, node as any);
        this.editor.view.dispatch(tr);
    }

    private plainTextToHtml(text: string): string {
        if (!text) return '';
        return text.split('\n').map(line => {
            if (!line) return '<p></p>';
            return `<p>${escapeHtml(line)}</p>`;
        }).join('');
    }

    private htmlToPlainText(html: string): string {
        if (!html) return '';
        // Convert structural HTML to plain text directly rather than relying on
        // innerText, which drops empty paragraphs on detached DOM elements.
        let result = html
            .replace(/<!--[\s\S]*?-->/g, '') // drop HTML comments
            .replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, '<p></p>') // strip placeholder <br> from empty paragraphs
            .replace(/<\/p>\s*<p[^>]*>/gi, '\n') // paragraph boundaries → newline
            .replace(/<li[^>]*>/gi, '- ') // list items → bullet
            .replace(/<\/(?:li|div|h[1-6]|tr|blockquote)>/gi, '\n') // block boundaries → newline
            .replace(/<br\s*\/?>/gi, '\n') // remaining <br> (shift+enter) → newline
            .replace(/<\/?[a-zA-Z][^>]*>/g, ''); // strip real tags only (keeps "a <= b")
        // Decode entities without touching the DOM (avoids an innerHTML XSS surface).
        result = unescapeHtml(result);
        // Convert non-breaking spaces (ProseMirror uses these for consecutive spaces) to regular spaces
        result = result.replace(/\u00A0/g, ' ');
        // ProseMirror always appends a trailing empty paragraph for editing;
        // strip the resulting trailing newline so it doesn't accumulate on each save-reload cycle.
        if (result.endsWith('\n')) result = result.slice(0, -1);
        return result;
    }
}

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatMenuModule,
        NgxEditorModule
    ],
    declarations: [TellerOnlineWysiwygEditorComponent],
    exports: [TellerOnlineWysiwygEditorComponent]
})
export class TellerOnlineWysiwygEditorModule { }
